<template>
  <div class="flex">
    <Button
      v-if="canSaveView"
      @click="toggleAddViewDialog"
      :label="t('components.buttons.save_as')"
      :disabled="!isFiltered"
      variant="tertiary"
      type="color"
      size="xs"
    />
    <Button
      v-if="!currentView.default"
      :disabled="!isFiltered"
      size="xs"
      variant="tertiary"
      type="color"
      :label="t('components.buttons.update')"
      @click="handleUpdate"
    />
    <Button
      @click="emit('cancel')"
      :label="t('components.buttons.cancel')"
      variant="tertiary"
      size="xs"
      type="gray"
    />
    <Add
      :open="isAddViewDialogOpen"
      :views="views"
      :name="name"
      :errorMessage="errorMessage"
      @update:name="name = $event"
      @update:open="toggleAddViewDialog"
      @cancel="toggleAddViewDialog"
      @submit="handleAdd"
    />
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { pickBy, isNil, isEmpty } from "lodash";
import { Button } from "@/SharedComponents/Buttons";
import { useI18n } from "vue-i18n";
import { Add } from "@/SharedComponents/PageHeader/Partials/CustomViewDialogs";
import { toast } from "@/SharedComponents/Toaster";

import {
  useAddCustomTasksView,
  useUpdateCustomTasksView,
} from "@/Composables/useTasksViews";

import {
  useAddCustomFilesView,
  useUpdateCustomFilesView,
} from "@/Composables/useFilesViews";
import { useFilters } from "@/Composables/useFilters";
import { storeToRefs } from "pinia";
import { useDefaultTasksViews } from "@/Pages/Tasks/Views";

const { t } = useI18n();

const isAddViewDialogOpen = ref(false);
const name = ref("");

const emit = defineEmits(["cancel", "addView", "updateView"]);

const props = defineProps({
  type: {
    type: String,
    required: true,
  },
  views: {
    type: Array,
    required: true,
  },
  currentView: {
    type: Object,
    required: true,
  },
  filterStates: {
    type: Object,
    required: true,
  },
});

const globalFilterStore = useFilters("global");
const { filterValues: globalFilterValues } = storeToRefs(globalFilterStore);

const { DefaultView } = useDefaultTasksViews();

const canSaveView = computed(() => {
  if (props.type === "assets" || props.type === "recurring_tasks") {
    return false;
  }

  return (
    props.currentView.default &&
    props.currentView.id !== DefaultView.ARCHIVED.id
  );
});

const toggleAddViewDialog = () => {
  if (isAddViewDialogOpen.value) {
    isAddViewDialogOpen.value = false;
    name.value = "";
  } else {
    isAddViewDialogOpen.value = true;
  }
};

const handleAdd = () => {
  const tenantId = globalFilterValues.value.tenant?.[0];

  if (props.type === "tasks") {
    createCustomTasksView({
      name: name.value,
      tenantId: tenantId,
      filterStates: props.filterStates,
    });
  }

  if (props.type === "files") {
    createCustomFilesView({
      name: name.value,
      tenantId: tenantId,
      filterStates: props.filterStates,
    });
  }
};

function handleUpdate() {
  if (props.type === "tasks") {
    updateCustomTasksView({
      id: props.currentView.id,
      name: props.currentView.name,
      filterStates: props.filterStates,
    });
  }

  if (props.type === "files") {
    updateCustomFilesView({
      id: props.currentView.id,
      name: props.currentView.name,
      filterStates: props.filterStates,
    });
  }
}

const { createCustomTasksView } = useAddCustomTasksView({
  onSuccess: (view) => {
    // Show the success toast
    toast.success(t("toasts.views.create.success"));

    // Close the dialog and reset the name
    isAddViewDialogOpen.value = false;
    name.value = "";

    // Emit add view
    emit("addView", view);
  },
  onError: () => {
    // Show the error toast
    toast.success(t("toasts.views.create.error"));

    // Close the dialog and reset the name
    isAddViewDialogOpen.value = false;
    name.value = "";
  },
});

const { createCustomFilesView } = useAddCustomFilesView({
  onSuccess: (view) => {
    // Show the success toast
    toast.success(t("toasts.views.create.success"));

    // Close the dialog and reset the name
    isAddViewDialogOpen.value = false;
    name.value = "";

    // Emit add view
    emit("addView", view);
  },
  onError: () => {
    // Show the error toast
    toast.success(t("toasts.views.create.error"));

    // Close the dialog and reset the name
    isAddViewDialogOpen.value = false;
    name.value = "";
  },
});

const { updateCustomTasksView } = useUpdateCustomTasksView({
  onSuccess: (view) => {
    // Show the success toast
    toast.success(t("toasts.views.update.success"));

    // Emit update view
    emit("updateView", view);
  },
  onError: (err) => {
    toast.error(err.response.data.message || t("toasts.views.update.error"));
  },
});

const { updateCustomFilesView } = useUpdateCustomFilesView({
  onSuccess: (view) => {
    // Show the success toast
    toast.success(t("toasts.views.update.success"));

    // Emit update view
    emit("updateView", view);
  },
  onError: (err) => {
    toast.error(err.response.data.message || t("toasts.views.update.error"));
  },
});

const isFiltered = computed(() => {
  let currentViewFilters =
    props.currentView.filters || props.currentView.criteria;

  return (
    JSON.stringify(
      pickBy(props.filterStates, (value) => !isNil(value) && !isEmpty(value))
    ) !==
    JSON.stringify(
      pickBy(currentViewFilters, (value) => !isNil(value) && !isEmpty(value))
    )
  );
});

const errorMessage = computed(() => {
  if (
    props.views.some(
      (view) => view.name.toLowerCase() === name.value.toLowerCase()
    )
  ) {
    return t("admin.views.errors.duplicate");
  }

  return "";
});
</script>
